import {
  ChakraFlex,
  GooglePlacesAutoCompleteV2,
  InputGroupV2,
  UniversalSelect,
} from '@equip.health/ui';

import { VITE_GOOGLE_PLACES_API_KEY } from '~/lib/constants/env';
import { LivesWithPatients } from '~/lib/constants/user.constants';
import useTimezone from '~/lib/hooks/useTimezone';
import { getSelectedUniversalSelectOption } from '~/lib/util/inquiry.util';
import {
  getFormattedOptions,
  patientEnumMenuOptionsMapper,
  patientStatesEnumMenuOptionsMapper,
} from '~/lib/util/patient.util';
import { getContactRequiredFields } from '~/lib/util/patientContact.util';
import {
  useGetProviderStatesEnumQuery,
  useGetProviderTimezonesEnumQuery,
} from '~/store/services/enumerations';

interface ContactAddressProps {
  contactAddress: ContactAddress;
  address: GooglePlacesField;
  updateField: (field: 'address', value: GooglePlacesField) => void;
  updateSection: (
    section: 'address' | 'contactAddress',
    field: keyof ContactAddress | keyof GooglePlacesField,
    value: string | string[] | boolean,
  ) => void;
}

const TOP_LEVEL_ID = 'contact-address';

const ContactAddress = ({
  contactAddress,
  updateField,
  address,
  updateSection,
}: ContactAddressProps) => {
  const livesWithPatientOptions = getFormattedOptions(LivesWithPatients);
  const { data: states, isLoading: isGetStatesLoading } =
    useGetProviderStatesEnumQuery();

  const statesMenuOptions = patientStatesEnumMenuOptionsMapper(states);
  const { data: timezones, isLoading: isGetTimezonesLoading } =
    useGetProviderTimezonesEnumQuery();
  const timezonesMenuOptions = patientEnumMenuOptionsMapper(timezones);
  const { getTimezoneFromLatLong, timezoneData } = useTimezone();

  const handleFieldChange = (
    field: keyof ContactAddress,
    value: string | string[] | boolean,
  ) => {
    updateSection('contactAddress', field, value);
  };

  return (
    <ChakraFlex flexDirection="column" gap="18px">
      <InputGroupV2
        id={`${TOP_LEVEL_ID}__lives-with-patient`}
        label="Lives with patient"
        flexGrow={0}
      >
        <UniversalSelect
          id={`${TOP_LEVEL_ID}__sex-select`}
          isSingleSelect
          onChange={(option) =>
            handleFieldChange('livesWithPatient', option?.value)
          }
          options={livesWithPatientOptions}
          value={getSelectedUniversalSelectOption(
            contactAddress.livesWithPatient,
            livesWithPatientOptions,
          )}
        />
      </InputGroupV2>
      <GooglePlacesAutoCompleteV2
        addressData={address}
        apiKey={VITE_GOOGLE_PLACES_API_KEY}
        getTimezone={getTimezoneFromLatLong}
        id={`${TOP_LEVEL_ID}__main-address`}
        isGetStatesLoading={isGetStatesLoading}
        isGetTimezonesLoading={isGetTimezonesLoading}
        label="Address/affiliation"
        organizationName={contactAddress.organization}
        requiredFields={getContactRequiredFields(address)}
        saveAddressData={(data: GooglePlacesField) =>
          updateField('address', data)
        }
        shouldShowAsteriskForLabel={
          getContactRequiredFields(address).length > 0
        }
        shouldShowLabel
        shouldShowOrganizationName
        shouldShowTimezone
        showAddressFields
        showUseMainAddressCheckbox={false}
        stateOptions={statesMenuOptions}
        timezoneData={timezoneData}
        timezoneOptions={timezonesMenuOptions}
        updateAddress={(
          value: string | { value: string; label: string },
          field: keyof GooglePlacesField,
        ) => {
          if (typeof value === 'object' && value !== null) {
            updateSection('address', field, value?.value);
          } else if (typeof value === 'string') {
            updateSection('address', field, value);
          } else {
            updateSection('address', field, '');
          }
        }}
        updateOrganizationName={(value: string) =>
          handleFieldChange('organization', value)
        }
      />
    </ChakraFlex>
  );
};

export default ContactAddress;
